import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import type { EventView } from '@goaltree/backend/convex/events'
import {
  CHECKIN_WINDOW_MIN,
  type CheckinSchedule,
  checkinStarts,
} from '@goaltree/backend/convex/lib/checkins'
import { eventRelative } from '@goaltree/backend/convex/lib/events'
import { recursDaysLabel } from '@goaltree/backend/convex/lib/schedule'
import {
  type UpcomingDay,
  type UpcomingItem,
  buildUpcoming,
  upcomingCount,
  upcomingItemDone,
} from '@goaltree/backend/convex/lib/upcoming'
import { cn } from '@goaltree/ui/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import { CalendarDays, CalendarPlus, MapPin, Repeat } from 'lucide-react'
import { createContext, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EventListRow } from '@/components/event-row'
import { InboxTaskDetailSheet, toSheetTask } from '@/components/inbox-task-detail-sheet'
import { TaskStatusIcon } from '@/components/task-status-icon'
import { UPCOMING_DAYS, useUpcomingWindow } from '@/components/today-schedule'
import { useCoachIdentity } from '@/hooks/use-coach-name'
import { useNow } from '@/hooks/use-now'
import { celebrateFrom } from '@/lib/celebrate'
import { addLocalDays, dayKeyOf, dayLabel, hhmm, relativeLabel } from '@/lib/events'
import { haptic } from '@/lib/haptics'
import { completeNode, uncompleteNode } from '@/lib/nodes-optimistic'
import { periodLabel } from '@/lib/schedule'
import { playDoneSound } from '@/lib/sounds'
import { undoToast } from '@/lib/undo-toast'
import { useEventSheet } from '@/store/event-sheet.store'
import { useViewStore } from '@/store/view.store'

const CALL_MS = CHECKIN_WINDOW_MIN * 60_000

type Upcoming = FunctionReturnType<typeof api.events.upcoming>
type DueTask = Upcoming['dueTasks'][number]
type RepeatTask = Upcoming['repeating'][number]
type Day = UpcomingDay<EventView, DueTask, RepeatTask>
type Item = UpcomingItem<EventView, DueTask, RepeatTask>

function useCheckinStarts(from: number, to: number) {
  const prefs = useQuery(api.preferences.getMyPreferences) as
    | { coachCheckins?: CheckinSchedule | null }
    | undefined
  return useMemo(
    () => (prefs === undefined ? undefined : checkinStarts(prefs.coachCheckins, from, to)),
    [prefs, from, to],
  )
}

function useCheckinDaysLabel(lang: string) {
  const prefs = useQuery(api.preferences.getMyPreferences) as
    | { coachCheckins?: CheckinSchedule | null }
    | undefined
  const days = prefs?.coachCheckins?.days
  return days?.length ? recursDaysLabel(days, lang) : null
}

const daySize = (day: Day) => day.items.length

const windowDays = (from: number, n: number) =>
  Array.from({ length: n }, (_, i) => {
    const start = addLocalDays(from, i)
    return { start, key: dayKeyOf(start) }
  })

type Block = { kind: 'day'; day: Day } | { kind: 'gap'; days: Day[] }

// The 14-day timeline, on the same subscription as Today's Coming up panel.
function useUpcoming() {
  const now = useNow(60_000)
  const { from, data } = useUpcomingWindow()
  const calls = useCheckinStarts(from, addLocalDays(from, UPCOMING_DAYS))
  const built = useMemo(
    () =>
      buildUpcoming({
        days: windowDays(from, UPCOMING_DAYS),
        keyOf: dayKeyOf,
        ...data,
        calls,
      }),
    [data, calls, from],
  )
  return { now, from, data, calls, ...built }
}

// Items still ahead in the next 14 days, for the tab title.
export function useUpcomingCount() {
  const { now, data, calls, days } = useUpcoming()
  if (!data || calls === undefined) return undefined
  return upcomingCount(days, now, dayKeyOf)
}

type TaskActions = {
  open: (id: Id<'nodes'>) => void
  toggle: (id: Id<'nodes'>, done: boolean) => void
}
const TaskActionsContext = createContext<TaskActions | null>(null)

// Ticks and opens tasks from the timeline the same way the Tasks tab does.
function TaskActionsProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation()
  const complete = useMutation(api.nodes.complete).withOptimisticUpdate((store, { taskId }) =>
    completeNode(store, taskId),
  )
  const uncomplete = useMutation(api.nodes.uncomplete).withOptimisticUpdate((store, { taskId }) =>
    uncompleteNode(store, taskId),
  )
  const [openId, setOpenId] = useState<Id<'nodes'> | null>(null)
  const [sheetMounted, setSheetMounted] = useState(false)
  const doc = useQuery(api.events.taskForSheet, openId ? { taskId: openId } : 'skip')
  const sheetTask = useMemo(() => (doc ? toSheetTask(doc) : null), [doc])
  const actions = useMemo<TaskActions>(
    () => ({
      open: (id) => {
        setSheetMounted(true)
        setOpenId(id)
      },
      toggle: (taskId, done) => {
        haptic()
        if (done) return void uncomplete({ taskId })
        playDoneSound()
        void complete({ taskId })
        undoToast(
          t('today.markedDone', { defaultValue: 'Marked done' }),
          () => void uncomplete({ taskId }),
        )
      },
    }),
    [complete, uncomplete, t],
  )
  return (
    <TaskActionsContext.Provider value={actions}>
      {children}
      {sheetMounted && (
        <InboxTaskDetailSheet
          task={sheetTask}
          onClose={() => setOpenId(null)}
          onComplete={(taskId) => void complete({ taskId })}
          onUncomplete={(taskId) => void uncomplete({ taskId })}
        />
      )}
    </TaskActionsContext.Provider>
  )
}

const LATER_CHUNKS = 6
const PAST_PAGE = 30

// Upcoming | Past, remembered per device.
export function EventsAgenda(props: AgendaProps) {
  const { t } = useTranslation()
  const mode = useViewStore((s) => s.eventsMode)
  const setMode = useViewStore((s) => s.setEventsMode)
  return (
    <>
      <div
        role="group"
        aria-label={t('upcoming.modeAria')}
        className="mt-3 flex w-fit gap-0.5 rounded-lg bg-muted/50 p-0.5"
      >
        {(['upcoming', 'past'] as const).map((m) => (
          <button
            key={m}
            type="button"
            aria-pressed={mode === m}
            onClick={() => setMode(m)}
            className={cn(
              'cursor-pointer rounded-md px-3 py-1 text-xs font-semibold transition-colors',
              mode === m
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {t(m === 'upcoming' ? 'upcoming.modeUpcoming' : 'upcoming.modePast')}
          </button>
        ))}
      </div>
      <TaskActionsProvider>
        {mode === 'past' ? <PastEvents activeId={props.activeId} /> : <UpcomingAgenda {...props} />}
      </TaskActionsProvider>
    </>
  )
}

function PastEvents({ activeId }: { activeId?: string | null }) {
  const { t, i18n } = useTranslation()
  const now = useNow(300_000)
  const [limit, setLimit] = useState(PAST_PAGE)
  const data = useQuery(api.events.past, { now, limit })
  const days = useMemo(() => {
    const out: { key: string; start: number; events: EventView[] }[] = []
    for (const e of data?.events ?? []) {
      const key = dayKeyOf(e.startsAt)
      const last = out[out.length - 1]
      if (last?.key === key) last.events.push(e)
      else out.push({ key, start: e.startsAt, events: [e] })
    }
    return out
  }, [data])

  if (!data) return null
  if (!days.length)
    return (
      <div className="flex flex-col items-center gap-2 py-14 text-center">
        <CalendarDays className="h-8 w-8 text-muted-foreground/50" strokeWidth={1.5} />
        <p className="text-sm font-semibold">{t('upcoming.pastEmptyTitle')}</p>
        <p className="max-w-xs text-sm text-muted-foreground">{t('upcoming.pastEmptyBody')}</p>
      </div>
    )
  return (
    <>
      <p className="px-2 pt-3 font-mono text-[11.5px] text-muted-foreground/70">
        {t('upcoming.pastRange')}
      </p>
      {days.map((day) => (
        <section key={day.key} className="pt-4">
          <h2 className="mb-1 px-2 text-sm font-bold">{dayLabel(day.start, i18n.language)}</h2>
          {day.events.map((event) => (
            <div
              key={event._id}
              data-agenda-event={event._id}
              className={cn(
                'rounded-lg transition-colors',
                event._id === activeId && 'bg-primary/10 ring-1 ring-primary/40',
              )}
            >
              <EventListRow event={event} now={now} />
            </div>
          ))}
        </section>
      ))}
      {data.hasMore && (
        <ShowMore label={t('upcoming.showMore')} onClick={() => setLimit((n) => n + PAST_PAGE)} />
      )}
    </>
  )
}

function ShowMore({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-4 w-full cursor-pointer rounded-lg py-2 text-center text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted/50 hover:text-foreground"
    >
      {label}
    </button>
  )
}

// Two-month slice of events starting on or after `from`, grouped by day.
function LaterChunk({
  from,
  now,
  activeId,
}: {
  from: number
  now: number
  activeId?: string | null
}) {
  const { t, i18n } = useTranslation()
  const name = useCoachIdentity().name
  const to = addLocalDays(from, 62)
  const data = useQuery(api.events.range, { from, to })
  const calls = useCheckinStarts(from, to)
  const days = useMemo(
    () =>
      buildUpcoming<EventView, DueTask, RepeatTask>({
        days: windowDays(from, 62),
        keyOf: dayKeyOf,
        events: (data ?? []).filter((e) => e.startsAt >= from),
        calls,
      }).days.filter(daySize),
    [data, calls, from],
  )
  if (!data || calls === undefined) return null
  const range = `${new Date(from).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })} – ${new Date(addLocalDays(from, 61)).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })}`
  if (!days.length)
    return (
      <p className="border-b border-border/50 py-3 font-mono text-[11.5px] text-muted-foreground/70">
        {t('upcoming.nothingPlanned', { range })}
      </p>
    )
  return (
    <>
      {days.map((day) => (
        <section key={day.key} className="pt-4">
          <h2 className="mb-1 px-2 text-sm font-bold">{dayLabel(day.start, i18n.language)}</h2>
          <DaySlots day={day} now={now} activeId={activeId} name={name} />
        </section>
      ))}
    </>
  )
}

type AgendaProps = {
  // YYYY-MM-DD of the filtered day, or null for the whole window.
  selected: string | null
  onSelect: (day: string | null) => void
  onAddEvent: () => void
  // Row selected with j/k or jumped to from search.
  activeId?: string | null
}

// 14-day events agenda: a day strip that filters to one day, then each day's
// events with quiet gaps between, then on-demand "Later" chunks.
function UpcomingAgenda({ selected, onSelect, onAddEvent, activeId }: AgendaProps) {
  const { t, i18n } = useTranslation()
  const { now, from, data, calls, days, overdue } = useUpcoming()
  const coachName = useCoachIdentity().name
  const [laterChunks, setLaterChunks] = useState(0)
  const eventsReady = !!data
  const scheduleReady = calls !== undefined

  const blocks = useMemo<Block[]>(() => {
    const out: Block[] = []
    for (const day of days) {
      const last = out[out.length - 1]
      if (daySize(day)) out.push({ kind: 'day', day })
      else if (last?.kind === 'gap') last.days.push(day)
      else out.push({ kind: 'gap', days: [day] })
    }
    return out
  }, [days])

  const selectedDay = days.find((d) => d.key === selected) ?? null
  const total = days.reduce((n, d) => n + daySize(d), 0) + overdue.length
  const locale = i18n.language
  const monthDay = (ms: number) =>
    new Date(ms).toLocaleDateString(locale, { month: 'short', day: 'numeric' })

  const addEventButton = (
    <button
      type="button"
      onClick={onAddEvent}
      className="flex h-8 shrink-0 cursor-pointer items-center gap-1.5 rounded-full bg-primary px-3.5 text-xs font-bold text-primary-foreground transition-colors hover:bg-primary/90"
    >
      <CalendarPlus className="size-3.5" strokeWidth={2.5} />
      {t('upcoming.addEvent')}
    </button>
  )

  const daySection = (day: Day, aside?: React.ReactNode) => {
    const offset = Math.round((day.start - from) / 86_400_000)
    const head =
      offset === 0
        ? t('upcoming.today')
        : offset === 1
          ? t('upcoming.tomorrow')
          : new Date(day.start).toLocaleDateString(locale, { weekday: 'long' })
    return (
      <section key={day.key} className="pt-4">
        <h2 className="mb-1 flex items-center gap-2 px-2">
          <b className="text-sm font-bold">{head}</b>
          <span className="font-mono text-[11.5px] text-muted-foreground">
            {offset <= 1 ? dayLabel(day.start, locale) : monthDay(day.start)}
          </span>
          {aside}
        </h2>
        <DaySlots day={day} now={now} activeId={activeId} name={coachName} />
      </section>
    )
  }

  const next = days
    .flatMap((day) => day.items)
    .find(
      (i): i is Extract<Item, { kind: 'event' | 'call' }> =>
        (i.kind === 'event' || i.kind === 'call') && !upcomingItemDone(i, now, dayKeyOf),
    )

  return (
    <>
      {next?.kind === 'event' ? (
        <NextEventLine event={next.event} now={now} from={from} />
      ) : next?.kind === 'call' ? (
        <CheckinNextLine startsAt={next.at} name={coachName} now={now} from={from} />
      ) : null}
      <div className="sticky top-(--app-hdr-h) z-10 -mx-4 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
        <div
          role="group"
          aria-label={t('upcoming.weekStrip')}
          className="flex snap-x snap-mandatory scroll-px-4 overflow-x-auto px-4 py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {days.map((day) => {
            const on = day.key === selectedDay?.key
            return (
              <button
                key={day.key}
                type="button"
                aria-pressed={on}
                aria-label={[
                  dayLabel(day.start, locale),
                  daySize(day) ? t('upcoming.dayEvents') : null,
                ]
                  .filter(Boolean)
                  .join(', ')}
                onClick={() => onSelect(on ? null : day.key)}
                className={cn(
                  'flex w-[calc(100%/7)] shrink-0 snap-start cursor-pointer flex-col items-center gap-0.5 rounded-xl py-1.5 text-[11px] text-muted-foreground transition-colors',
                  on ? 'bg-primary text-primary-foreground' : 'hover:bg-muted/60',
                )}
              >
                {new Date(day.start).toLocaleDateString(locale, { weekday: 'short' })}
                <b
                  className={cn(
                    'text-base leading-tight',
                    on
                      ? 'text-primary-foreground'
                      : day.start === from
                        ? 'text-primary-text'
                        : 'text-foreground',
                  )}
                >
                  {new Date(day.start).getDate()}
                </b>
                <span className="flex h-1.5">
                  {daySize(day) > 0 && (
                    <i
                      className={cn(
                        'h-1.5 w-1.5 rounded-full',
                        on ? 'bg-primary-foreground' : 'bg-primary',
                      )}
                    />
                  )}
                </span>
              </button>
            )
          })}
        </div>
      </div>

      {!eventsReady ? null : selectedDay ? (
        daySize(selectedDay) ? (
          daySection(selectedDay)
        ) : !scheduleReady ? null : (
          <>
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <CalendarDays className="h-8 w-8 text-muted-foreground/50" strokeWidth={1.5} />
              <p className="text-sm font-semibold">
                {t('upcoming.nothingOn', {
                  day: new Date(selectedDay.start).toLocaleDateString(locale, {
                    weekday: 'short',
                    day: 'numeric',
                  }),
                })}
              </p>
              {addEventButton}
            </div>
          </>
        )
      ) : !scheduleReady && total === 0 ? null : total === 0 ? (
        <div className="flex flex-col items-center gap-2 py-14 text-center">
          <CalendarDays className="h-8 w-8 text-muted-foreground/50" strokeWidth={1.5} />
          <p className="text-sm font-semibold">{t('upcoming.noEventsTitle')}</p>
          <p className="mb-1 max-w-xs text-sm text-muted-foreground">
            {t('upcoming.noEventsBody')}
          </p>
          {addEventButton}
        </div>
      ) : (
        <>
          {overdue.length > 0 && (
            <section className="pt-4">
              <h2 className="mb-1 px-2 text-sm font-bold text-destructive">
                {t('upcoming.overdue')}
              </h2>
              {overdue.map((task) => (
                <TaskRow
                  key={task._id}
                  item={{ kind: 'task', at: task.dueDate, untimed: task.allDay, task }}
                  now={now}
                  overdue
                />
              ))}
            </section>
          )}
          {blocks.map((block) => {
            if (block.kind === 'day') return daySection(block.day)
            const first = block.days[0].start
            const last = block.days[block.days.length - 1].start
            const range =
              first === last ? dayLabel(first, locale) : `${monthDay(first)} – ${monthDay(last)}`
            return (
              <p
                key={block.days[0].key}
                className="border-b border-border/50 py-3 font-mono text-[11.5px] text-muted-foreground/70"
              >
                {t('upcoming.nothingPlanned', { range })}
              </p>
            )
          })}
        </>
      )}

      {eventsReady && (scheduleReady || total > 0) && !selectedDay && (
        <section className="pt-6">
          {laterChunks === 0 ? (
            <ShowMore label={t('upcoming.showLater')} onClick={() => setLaterChunks(1)} />
          ) : (
            <>
              <h2 className="px-2 text-sm font-bold">{t('upcoming.later')}</h2>
              {Array.from({ length: laterChunks }, (_, i) => (
                <LaterChunk
                  key={i}
                  from={addLocalDays(from, UPCOMING_DAYS + 62 * i)}
                  now={now}
                  activeId={activeId}
                />
              ))}
              {laterChunks < LATER_CHUNKS && (
                <ShowMore
                  label={t('upcoming.showMore')}
                  onClick={() => setLaterChunks((n) => n + 1)}
                />
              )}
            </>
          )}
        </section>
      )}
    </>
  )
}

function DaySlots({
  day,
  now,
  activeId,
  name,
}: {
  day: Day
  now: number
  activeId?: string | null
  name: string
}) {
  return day.items.map((item) =>
    item.kind === 'event' ? (
      <div
        key={item.event._id}
        data-agenda-event={item.event._id}
        className={cn(
          'rounded-lg transition-colors',
          item.event._id === activeId && 'bg-primary/10 ring-1 ring-primary/40',
        )}
      >
        <EventListRow event={item.event} now={now} />
      </div>
    ) : item.kind === 'call' ? (
      <CheckinRow key={`call-${item.at}`} startsAt={item.at} name={name} now={now} />
    ) : (
      <TaskRow
        key={item.kind === 'repeat' ? `${item.task._id}-${item.day}` : item.task._id}
        item={item}
        now={now}
      />
    ),
  )
}

// A dated or recurring task on the timeline: tick it, or open its sheet.
function TaskRow({
  item,
  now,
  overdue,
}: {
  item: Extract<Item, { kind: 'task' | 'repeat' }>
  now: number
  overdue?: boolean
}) {
  const { t, i18n } = useTranslation()
  const actions = useContext(TaskActionsContext)
  const { task } = item
  const done = upcomingItemDone(item, now, dayKeyOf)
  const tickable = item.kind === 'task' || item.tickable
  const time = overdue
    ? new Date(item.at).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })
    : item.untimed
      ? t('upcoming.due')
      : hhmm(item.at)
  const period =
    item.kind === 'repeat' &&
    periodLabel(t, i18n.language, item.task.recursPeriod, item.task.recursDays)
  return (
    <div className="flex min-h-12 w-full min-w-0 items-center gap-2.5 rounded-lg px-2 py-2 transition-colors hover:bg-muted/50">
      <span
        className={cn(
          'w-12 shrink-0 font-mono text-[11.5px] tabular-nums',
          item.untimed && !overdue && 'text-[10px] uppercase tracking-wide',
          overdue
            ? 'text-destructive'
            : done
              ? 'text-muted-foreground/60'
              : 'text-muted-foreground',
        )}
      >
        {time}
      </span>
      {tickable ? (
        <button
          type="button"
          role="checkbox"
          aria-checked={done}
          aria-label={t('todaySchedule.markDone', { title: task.title })}
          onClick={(e) => {
            if (!done) celebrateFrom(e.currentTarget)
            actions?.toggle(task._id, done)
          }}
          className="-m-1.5 shrink-0 cursor-pointer rounded-full p-1.5 outline-none transition-colors hover:bg-primary/10 focus-visible:ring-2 focus-visible:ring-ring/60"
        >
          <TaskStatusIcon status={done ? 'done' : 'pending'} size={20} />
        </button>
      ) : (
        <span
          aria-hidden
          className="flex size-5 shrink-0 items-center justify-center text-muted-foreground/60"
        >
          <Repeat className="size-3.5" strokeWidth={2.25} />
        </span>
      )}
      <button
        type="button"
        onClick={() => actions?.open(task._id)}
        aria-label={t('upcoming.openTask', { title: task.title })}
        className="min-w-0 flex-1 cursor-pointer text-left"
      >
        <span
          className={cn(
            'block truncate text-sm font-semibold',
            done && 'text-muted-foreground line-through decoration-muted-foreground/50',
          )}
        >
          {task.title}
        </span>
        {(period || task.location) && (
          <span className="flex min-w-0 items-center gap-1 font-mono text-[11px] text-muted-foreground">
            {period && (
              <>
                <Repeat className="h-2.5 w-2.5 shrink-0" />
                <span className="shrink-0">{period}</span>
              </>
            )}
            {task.location && (
              <>
                <MapPin className="h-2.5 w-2.5 shrink-0" />
                <span className="truncate">{task.location}</span>
              </>
            )}
          </span>
        )}
      </button>
    </div>
  )
}

function CheckinRow({ startsAt, name, now }: { startsAt: number; name: string; now: number }) {
  const { t, i18n } = useTranslation()
  const days = useCheckinDaysLabel(i18n.language)
  const navigate = useNavigate()
  const past = startsAt + CALL_MS <= now
  const title = t('coachCall.eventTitle', { name })
  const time = hhmm(startsAt)
  return (
    <button
      type="button"
      onClick={() => void navigate({ to: '/settings', hash: 'coach' })}
      aria-label={`${title}, ${time}`}
      className="flex min-h-12 w-full min-w-0 cursor-pointer items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-muted/50"
    >
      <span
        className={cn(
          'w-12 shrink-0 font-mono text-[11.5px] tabular-nums',
          past ? 'text-muted-foreground/60' : 'text-muted-foreground',
        )}
      >
        {time}
      </span>
      <span
        className={cn(
          'h-8 w-[3px] shrink-0 rounded-full bg-primary',
          past ? 'opacity-25' : 'opacity-75',
        )}
      />
      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block truncate text-sm font-semibold',
            past && 'text-muted-foreground line-through decoration-muted-foreground/50',
          )}
        >
          {title}
        </span>
        <span className="flex min-w-0 items-center gap-1 font-mono text-[11px] text-muted-foreground">
          {days && (
            <>
              <Repeat className="h-2.5 w-2.5 shrink-0" />
              <span className="shrink-0">{days} ·</span>
            </>
          )}
          <span className="truncate">{t('coachCall.eventHint')}</span>
        </span>
      </span>
    </button>
  )
}

function CheckinNextLine({
  startsAt,
  name,
  now,
  from,
}: {
  startsAt: number
  name: string
  now: number
  from: number
}) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const rel = eventRelative({ startsAt, durationMinutes: CHECKIN_WINDOW_MIN }, now)
  const today = startsAt < addLocalDays(from, 1)
  const when = [
    !today &&
      new Date(startsAt).toLocaleDateString(i18n.language, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      }),
    hhmm(startsAt),
    today && rel.kind !== 'allDay' && relativeLabel(rel, t),
  ]
    .filter(Boolean)
    .join(' · ')
  const title = t('coachCall.eventTitle', { name })
  return (
    <button
      type="button"
      onClick={() => void navigate({ to: '/settings', hash: 'coach' })}
      className="mt-1 flex w-full min-w-0 cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted/50"
    >
      <span className="shrink-0 text-[10px] font-bold tracking-widest text-primary-text uppercase">
        {rel.kind === 'now' ? t('events.nowLabel') : t('events.nextLabel')}
      </span>
      <span className="min-w-0 truncate font-semibold">{title}</span>
      <span className="ml-auto shrink-0 font-mono text-xs text-muted-foreground">{when}</span>
    </button>
  )
}

// "Next · Dentist · Thu 15:00" — relative time only once it's today.
function NextEventLine({ event, now, from }: { event: EventView; now: number; from: number }) {
  const { t, i18n } = useTranslation()
  const rel = eventRelative(event, now)
  const today = event.startsAt < addLocalDays(from, 1)
  const when = [
    !today &&
      new Date(event.startsAt).toLocaleDateString(i18n.language, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
      }),
    event.allDay ? t('events.allDay') : hhmm(event.startsAt),
    today && rel.kind !== 'allDay' && relativeLabel(rel, t),
  ]
    .filter(Boolean)
    .join(' · ')
  return (
    <button
      type="button"
      onClick={() => useEventSheet.getState().open(event._id)}
      className="mt-1 flex w-full min-w-0 cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted/50"
    >
      <span className="shrink-0 text-[10px] font-bold uppercase tracking-widest text-primary-text">
        {rel.kind === 'now' ? t('events.nowLabel') : t('events.nextLabel')}
      </span>
      <span className="min-w-0 truncate font-semibold">{event.title}</span>
      <span className="ml-auto shrink-0 font-mono text-xs text-muted-foreground">{when}</span>
    </button>
  )
}
